import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../../lib/api.js'
import { supabase } from '../../lib/supabase.js'
import { MIN_CIRCUIT_CASES, MAX_CIRCUIT_CASES, BONUS_RATIO } from '../../lib/constants.js'

const EDUCATION_LEVELS = [
  'CP', 'CE1', 'CE2', 'CM1', 'CM2',
  '6ème', '5ème', '4ème', '3ème', 'CAP',
  '2nde', '1ère', 'Terminale',
  'BTS/BUT', 'Licence', 'Master',
  'Formation continue', 'Non applicable',
]
const SUBJECT_OPTIONS = [
  'Allemand', 'Anglais', 'Arts Plastiques', 'Autre langue vivante',
  'Chimie', 'Économie-Gestion', 'EMC', 'EPS', 'Espagnol',
  'Français', 'Géographie', 'Grec', 'Histoire',
  'Informatique', 'Italien', 'Latin',
  'Mathématiques', 'Musique', 'Philosophie', 'Physique',
  'SES', 'SVT', 'Technologie',
  'Autre',
]
const DIFFICULTY_OPTIONS = ['Simple', 'Intermédiaire', 'Difficile']

const DIFF_BTN_ACTIVE = {
  'Simple':        'bg-blue-600 text-white border-blue-600',
  'Intermédiaire': 'bg-amber-500 text-white border-amber-500',
  'Difficile':     'bg-red-600 text-white border-red-600',
}

function KeywordsInput({ value, onChange }) {
  const [input, setInput] = useState('')
  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      const tag = input.trim()
      if (tag && !value.includes(tag)) onChange([...value, tag])
      setInput('')
    }
  }
  function remove(tag) { onChange(value.filter(t => t !== tag)) }
  return (
    <div className="border border-white/20 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500 bg-white/10 flex flex-wrap gap-1 min-h-[42px]">
      {value.map(tag => (
        <span key={tag} className="flex items-center gap-1 bg-blue-500/20 text-blue-300 text-sm px-2 py-0.5 rounded-full">
          {tag}
          <button type="button" onClick={() => remove(tag)} className="hover:text-red-400 ml-0.5 font-semibold leading-none">×</button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? 'Appuie sur Entrée pour ajouter un mot-clé' : ''}
        className="outline-none flex-1 min-w-[120px] text-sm py-0.5 bg-transparent text-white placeholder-white/40"
      />
    </div>
  )
}

// Composant partagé pour la création et l'édition d'un deck.
// Utilisé par NewDeckPage (host), EditDeckPage (host), et les routes admin.
// navBase : '/host/decks' ou '/admin/decks'
export default function DeckFormContent({ navBase }) {
  const { deckId } = useParams()
  const isEditing = Boolean(deckId)
  const navigate = useNavigate()

  const [themes, setThemes] = useState([])
  const [musiques, setMusiques] = useState([])
  const [previewPlayingId, setPreviewPlayingId] = useState(null)
  const previewAudioRef = useRef(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [form, setForm] = useState(null)
  const [postAction, setPostAction] = useState('generate')
  const [kahootUrl, setKahootUrl] = useState('')
  const [kahootError, setKahootError] = useState('')
  const [kahootImportImages, setKahootImportImages] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [loadError, setLoadError] = useState('')
  const [phase, setPhase] = useState('form') // 'form' | 'kahoot-loading'

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    const promises = [apiFetch('/api/themes'), apiFetch('/api/musiques')]
    if (isEditing) promises.push(apiFetch(`/api/decks/${deckId}`))

    Promise.all(promises).then(([themesData, musiquesData, deck]) => {
      setThemes(themesData)
      setMusiques(musiquesData)
      const defaultThemeId = themesData.find(t => t.is_default)?.id ?? null
      if (isEditing && deck) {
        setForm({
          titre: deck.titre ?? '',
          keywords: deck.keywords ?? [],
          matiere: deck.matiere ?? '',
          education_level: deck.education_level ?? 'Non applicable',
          difficulty: deck.difficulty ?? 'Intermédiaire',
          visibility: deck.visibility ?? 'privé',
          publishable_status: deck.publishable_status ?? 'A définir',
          theme_visuel: deck.theme_visuel ?? defaultThemeId,
          nb_cases: deck.nb_cases ?? 30,
          nb_cases_bonus: deck.nb_cases_bonus ?? 6,
          timer_max: deck.timer_max ?? 30,
          timer_min: deck.timer_min ?? 5,
          musique_fond_id: deck.musique_fond_id ?? null,
        })
      } else {
        setForm({
          titre: '',
          keywords: [],
          matiere: '',
          education_level: 'Non applicable',
          difficulty: 'Intermédiaire',
          visibility: 'privé',
          publishable_status: 'A définir',
          theme_visuel: defaultThemeId,
          nb_cases: 30,
          nb_cases_bonus: 6,
          timer_max: 30,
          timer_min: 5,
          musique_fond_id: null,
        })
      }
    }).catch(e => setLoadError(e.message))

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return
      supabase.from('profiles').select('role').eq('id', session.user.id).single()
        .then(({ data }) => setIsAdmin(data?.role === 'admin'))
    })

    return () => {
      previewAudioRef.current?.pause()
      previewAudioRef.current = null
    }
  }, [deckId, isEditing])

  function handleMusiquePreview(musique) {
    if (previewPlayingId === musique.id) {
      previewAudioRef.current?.pause()
      previewAudioRef.current = null
      setPreviewPlayingId(null)
      return
    }
    previewAudioRef.current?.pause()
    previewAudioRef.current = null
    const audio = new Audio(musique.url)
    audio.addEventListener('ended', () => setPreviewPlayingId(null))
    audio.play().catch(() => {})
    previewAudioRef.current = audio
    setPreviewPlayingId(musique.id)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    setKahootError('')

    try {
      if (!isEditing && postAction === 'kahoot') {
        const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
        if (!uuidRegex.test(kahootUrl)) {
          setKahootError('URL invalide — copiez l\'URL depuis create.kahoot.it/details/...')
          setSaving(false)
          return
        }
        if (/\/details\/duplicate-of-/.test(kahootUrl)) {
          setKahootError('Ce kahoot est une copie privée — rendez-le public sur Kahoot.it (Paramètres → Visibilité → Public) ou utilisez l\'URL du quiz original.')
          setSaving(false)
          return
        }
        setPhase('kahoot-loading')
        const data = await apiFetch('/api/decks/import/kahoot', {
          method: 'POST',
          body: JSON.stringify({ ...form, url: kahootUrl, importImages: kahootImportImages }),
        })
        const msg = `${data.imported} question${data.imported > 1 ? 's' : ''} importée${data.imported > 1 ? 's' : ''}${data.skipped > 0 ? `, ${data.skipped} ignorée${data.skipped > 1 ? 's' : ''} (format incompatible)` : ''}`
        navigate(navBase, { state: { snackbar: msg } })
        return
      }

      if (isEditing) {
        await apiFetch(`/api/decks/${deckId}`, { method: 'PUT', body: JSON.stringify(form) })
        navigate(navBase)
      } else {
        const newDeck = await apiFetch('/api/decks', { method: 'POST', body: JSON.stringify(form) })
        if (postAction === 'generate') {
          navigate(`${navBase}/${newDeck.id}/generate`)
        } else {
          navigate(`${navBase}/${newDeck.id}`)
        }
      }
    } catch (e) {
      setError(e.message)
      setPhase('form')
    }
    setSaving(false)
  }

  if (phase === 'kahoot-loading') {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <svg className="animate-spin h-10 w-10 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <p className="text-lg font-medium text-white">Import en cours…</p>
        <p className="text-sm text-white/50">Récupération du quiz depuis Kahoot</p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm">{loadError}</div>
    )
  }

  if (!form) {
    return (
      <div className="flex justify-center py-24">
        <svg className="animate-spin h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">
          Titre <span className="text-red-400">*</span>
        </label>
        <input
          required
          value={form.titre}
          onChange={e => set('titre', e.target.value)}
          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="ex. Géographie - Capitales"
        />
      </div>

      {!isEditing && (
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">Contenu du quiz</label>
          <div className="border border-white/20 rounded-lg overflow-hidden divide-y divide-white/10">
            <button
              type="button"
              onClick={() => setPostAction('generate')}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${
                postAction === 'generate' ? 'bg-blue-500/15' : 'hover:bg-white/5'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                postAction === 'generate' ? 'border-blue-400' : 'border-white/30'
              }`}>
                {postAction === 'generate' && <div className="w-2 h-2 rounded-full bg-blue-400" />}
              </div>
              <div>
                <p className="font-medium text-white">🪄 Générer les questions par IA</p>
                <p className="text-xs text-white/50">L'IA crée les sous-thèmes et les questions</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setPostAction('manual')}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${
                postAction === 'manual' ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                postAction === 'manual' ? 'border-blue-400' : 'border-white/30'
              }`}>
                {postAction === 'manual' && <div className="w-2 h-2 rounded-full bg-blue-400" />}
              </div>
              <div>
                <p className="font-medium text-white">✏️ Créer manuellement mes questions</p>
                <p className="text-xs text-white/50">Ajoute tes questions une par une</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setPostAction('kahoot')}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${
                postAction === 'kahoot' ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                postAction === 'kahoot' ? 'border-blue-400' : 'border-white/30'
              }`}>
                {postAction === 'kahoot' && <div className="w-2 h-2 rounded-full bg-blue-400" />}
              </div>
              <div>
                <p className="font-medium text-white">📥 Importer depuis Kahoot</p>
                <p className="text-xs text-white/50">Récupérer les questions d'un quiz public Kahoot</p>
              </div>
            </button>
          </div>
          {postAction === 'kahoot' && (
            <div className="mt-3 space-y-3">
              <div className="space-y-1">
                <input
                  type="url"
                  value={kahootUrl}
                  onChange={e => { setKahootUrl(e.target.value); setKahootError('') }}
                  placeholder="https://create.kahoot.it/details/......."
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {kahootError && <p className="text-xs text-red-400">{kahootError}</p>}
              </div>
              <div className="flex items-center justify-between py-1">
                <div>
                  <p className="text-sm font-medium text-white/80">Import des questions avec images</p>
                  <p className="text-xs text-white/40">
                    {kahootImportImages
                      ? 'Les questions Kahoot avec images sont importées'
                      : 'Les questions Kahoot avec images ne sont pas importées'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={kahootImportImages}
                  onClick={() => setKahootImportImages(v => !v)}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    kahootImportImages ? 'bg-blue-500' : 'bg-white/20'
                  }`}
                >
                  <span className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${
                    kahootImportImages ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Mots-clés</label>
        <KeywordsInput value={form.keywords ?? []} onChange={v => set('keywords', v)} />
        <p className="text-xs text-white/40 mt-1">Appuie sur Entrée pour valider chaque mot-clé.</p>
        {!isEditing && <p className="text-xs text-white/40 mt-0.5">Les mots clés aident à chercher et trouver les quiz.</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Matière</label>
        <select
          value={form.matiere ?? ''}
          onChange={e => set('matiere', e.target.value)}
          className="w-full bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-gray-800 [&>option]:text-white"
          style={{ colorScheme: 'dark' }}
        >
          <option value="">— Sélectionner —</option>
          {SUBJECT_OPTIONS.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">
          Niveau scolaire <span className="text-red-400">*</span>
        </label>
        <select
          required
          value={form.education_level}
          onChange={e => set('education_level', e.target.value)}
          className="w-full bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-gray-800 [&>option]:text-white"
          style={{ colorScheme: 'dark' }}
        >
          {EDUCATION_LEVELS.map(lvl => (
            <option key={lvl} value={lvl}>{lvl}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Difficulté</label>
        <div className="flex rounded-lg border border-white/20 overflow-hidden">
          {DIFFICULTY_OPTIONS.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => set('difficulty', opt)}
              className={`flex-1 py-2 text-sm font-medium transition border-r last:border-r-0 border-white/20 ${
                form.difficulty === opt
                  ? DIFF_BTN_ACTIVE[opt]
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        {!isEditing && <p className="text-xs text-white/40 mt-1">Indication du niveau de difficulté pour les joueurs.</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">Thème</label>
        <div
          className="flex gap-3 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible md:pb-0"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {themes.map(t => {
            const selected = form.theme_visuel === t.id
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => set('theme_visuel', t.id)}
                className={`shrink-0 w-36 h-20 md:w-full rounded-xl border-2 overflow-hidden relative transition ${
                  selected ? 'border-blue-400 ring-2 ring-blue-400/40' : 'border-white/20 hover:border-white/40'
                }`}
              >
                {t.image_url
                  ? <img src={t.image_url} alt={t.label} className="absolute inset-0 w-full h-full object-cover" />
                  : <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-600" />
                }
                <div className="absolute inset-0 bg-black/40" />
                {selected && (
                  <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-blue-400 rounded-full flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5">
                  <p className="text-white text-xs font-medium leading-tight line-clamp-1">{t.label}</p>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">Musique de fond</label>
        {musiques.length === 0 ? (
          <p className="text-xs text-white/40">Aucune musique disponible. Ajoutes-en depuis la section Musiques.</p>
        ) : (
          <div
            className="flex gap-3 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible md:pb-0"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <button
              type="button"
              onClick={() => set('musique_fond_id', null)}
              className={`shrink-0 w-36 h-20 md:w-full rounded-xl border-2 overflow-hidden relative transition flex flex-col items-center justify-center gap-1 bg-white/5 ${
                !form.musique_fond_id
                  ? 'border-blue-400 ring-2 ring-blue-400/40'
                  : 'border-white/20 hover:border-white/40'
              }`}
            >
              <span className="text-xl opacity-50">🔇</span>
              <p className="text-white/60 text-xs font-medium">Aucune</p>
              {!form.musique_fond_id && (
                <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-blue-400 rounded-full flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
            {musiques.map(m => {
              const selected = form.musique_fond_id === m.id
              const playing = previewPlayingId === m.id
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => set('musique_fond_id', m.id)}
                  className={`shrink-0 w-36 h-20 md:w-full rounded-xl border-2 overflow-hidden relative transition ${
                    selected ? 'border-blue-400 ring-2 ring-blue-400/40' : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 to-purple-950" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-3xl ${playing ? 'animate-pulse' : 'opacity-30'}`}>🎵</span>
                  </div>
                  <div
                    onClick={e => { e.stopPropagation(); handleMusiquePreview(m) }}
                    className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-full flex items-center justify-center text-xs transition cursor-pointer ${
                      playing ? 'bg-blue-500 text-white' : 'bg-white/20 text-white/70 hover:bg-white/30 hover:text-white'
                    }`}
                    title={playing ? 'Arrêter' : 'Écouter'}
                  >
                    {playing ? '⏹' : '▶'}
                  </div>
                  {selected && (
                    <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-blue-400 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium leading-tight line-clamp-1">{m.titre}</p>
                  </div>
                </button>
              )
            })}
          </div>
        )}
        <p className="text-xs text-white/40 mt-1">Jouée en boucle côté hôte pendant la course.</p>
      </div>

      {/* Paramètres avancés */}
      <div className="border border-white/10 rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedOpen(o => !o)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition"
        >
          <span>Paramètres avancés</span>
          <svg
            className={`w-4 h-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {advancedOpen && (
          <div className="px-4 pb-4 pt-2 space-y-4 border-t border-white/10">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Timer max <span className="text-white/40 font-normal">(s)</span>
              </label>
              <input
                type="number" min={5} max={120}
                value={form.timer_max}
                onChange={e => set('timer_max', parseInt(e.target.value))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ colorScheme: 'dark' }}
              />
              <p className="text-xs text-white/40 mt-1">Durée maximum pour répondre à 1 question.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Timer min <span className="text-white/40 font-normal">(s)</span>
              </label>
              <input
                type="number" min={1} max={30}
                value={form.timer_min}
                onChange={e => set('timer_min', parseInt(e.target.value))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ colorScheme: 'dark' }}
              />
              <p className="text-xs text-white/40 mt-1">Délai minimum avant que la validation soit possible.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-white/80">Longueur du circuit</label>
                <span className="text-sm font-semibold text-white">
                  {form.nb_cases} cases · {form.nb_cases_bonus} bonus
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/40 w-6 text-center">{MIN_CIRCUIT_CASES}</span>
                <input
                  type="range"
                  min={MIN_CIRCUIT_CASES}
                  max={MAX_CIRCUIT_CASES}
                  value={form.nb_cases}
                  onChange={e => {
                    const n = parseInt(e.target.value)
                    set('nb_cases', n)
                    set('nb_cases_bonus', Math.max(1, Math.round(n * BONUS_RATIO)))
                  }}
                  className="flex-1 accent-blue-500"
                />
                <span className="text-xs text-white/40 w-6 text-center">{MAX_CIRCUIT_CASES}</span>
              </div>
              <p className="text-xs text-white/40 mt-1">Cases bonus calculées automatiquement (ratio 20%).</p>
            </div>

          </div>
        )}
      </div>

      <div className="flex items-center justify-between py-1">
        <div>
          <p className="text-sm font-medium text-white/80">Visibilité</p>
          <p className="text-xs text-white/40">
            {form.visibility === 'public' ? 'Visible par tous les hôtes' : 'Visible par toi uniquement'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={form.visibility === 'public'}
          onClick={() => set('visibility', form.visibility === 'public' ? 'privé' : 'public')}
          className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
            form.visibility === 'public' ? 'bg-blue-500' : 'bg-white/20'
          }`}
        >
          <span className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${
            form.visibility === 'public' ? 'translate-x-5' : 'translate-x-0'
          }`} />
        </button>
      </div>

      {isAdmin && (
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">
            Statut de publication <span className="text-xs text-white/40">(admin)</span>
          </label>
          <select
            value={form.publishable_status}
            onChange={e => set('publishable_status', e.target.value)}
            className="w-full bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-gray-800 [&>option]:text-white"
            style={{ colorScheme: 'dark' }}
          >
            {['A définir', 'Oui', 'Non'].map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex gap-3 justify-end pt-2 pb-8">
        <button
          type="button"
          onClick={() => navigate(navBase)}
          className="px-4 py-2 text-white/60 hover:text-white transition"
        >
          Annuler
        </button>
        <button type="submit" disabled={saving} className="btn-primary px-6 py-2 disabled:opacity-60">
          {saving
            ? 'Enregistrement…'
            : isEditing
              ? 'Enregistrer'
              : postAction === 'generate'
                ? 'Suivant →'
                : postAction === 'kahoot'
                  ? 'Importer'
                  : 'Enregistrer'}
        </button>
      </div>
    </form>
  )
}
